import { create } from 'zustand';
import { getPitayaClient } from '@/network/PitayaClient';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { getGame } from '@/game';
import data from '@/data/teleport.json';

export type MapType = 'town' | 'field' | 'dungeon' | 'event';

export interface TeleportDest {
  mapId: number;
  key: string;
  name: string;
  type: MapType;
  minLevel: number;
  scene: 'Town' | 'Field' | 'Dungeon';
  spawnX: number;
  spawnY: number;
  desc: string;
  highlights: string[];
}

interface TeleportConfig {
  baseCost: number;
  levelMultiplier: number;
  typeFactor: Record<MapType, number>;
  channelMs: number;
  townScrollCooldownMs: number;
  destinations: TeleportDest[];
}

const cfg = data as unknown as TeleportConfig;

export function listDestinations(): TeleportDest[] {
  return cfg.destinations;
}

export function teleportCost(d: TeleportDest): number {
  return Math.round(cfg.baseCost + d.minLevel * cfg.levelMultiplier * (cfg.typeFactor[d.type] ?? 1));
}

export function getChannelMs(): number {
  return cfg.channelMs;
}

export function getTownScrollCooldownMs(): number {
  return cfg.townScrollCooldownMs;
}

interface TeleportState {
  visitedMaps: Set<number>;
  channeling: { mapId: number; endsAt: number } | null;
  townScrollEndsAt: number;
  lastTeleportAt: number;
  markVisited: (mapId: number) => void;
  isVisited: (mapId: number) => boolean;
  beginChannel: (mapId: number, ms: number) => void;
  cancelChannel: () => void;
  requestTeleport: (mapId: number) => Promise<void>;
  useTownScroll: () => Promise<void>;
}

const TELEPORT_ROUTE_TOWN = 'town.teleport';
const TELEPORT_ROUTE_FIELD = 'field.teleport';

interface TeleportResp {
  ok: boolean;
  mapId: number;
  spawnX: number;
  spawnY: number;
  newZen: number;
  reason?: string;
}

function currentRoute(): string {
  // Determine which room we're in by current scene name.
  const game = getGame();
  const active = game?.scene.getScenes(true)[0]?.scene.key ?? '';
  if (active === 'Town') return TELEPORT_ROUTE_TOWN;
  return TELEPORT_ROUTE_FIELD;
}

export const useTeleportStore = create<TeleportState>((set, get) => ({
  visitedMaps: new Set([1]),
  channeling: null,
  townScrollEndsAt: 0,
  lastTeleportAt: 0,
  markVisited: (mapId) => set((s) => {
    if (s.visitedMaps.has(mapId)) return s;
    const next = new Set(s.visitedMaps);
    next.add(mapId);
    return { visitedMaps: next };
  }),
  isVisited: (mapId) => get().visitedMaps.has(mapId),
  beginChannel: (mapId, ms) => set({ channeling: { mapId, endsAt: Date.now() + ms } }),
  cancelChannel: () => set({ channeling: null }),

  requestTeleport: async (mapId) => {
    const dest = cfg.destinations.find((d) => d.mapId === mapId);
    if (!dest) return;

    const ch = useCharacterStore.getState().current;
    const zen = useCharacterStore.getState().zen;
    const cost = teleportCost(dest);

    if (!ch) return;
    if (ch.level < dest.minLevel) {
      useUIStore.getState().showToast(`Yêu cầu Lv ${dest.minLevel}`, 'warn');
      return;
    }
    if (zen < cost) {
      useUIStore.getState().showToast(`Không đủ Zen (cần ${cost.toLocaleString()})`, 'warn');
      return;
    }
    if (Date.now() - get().lastTeleportAt < 2000) {
      useUIStore.getState().showToast('Teleport quá nhanh, hãy chờ', 'warn');
      return;
    }
    if (ch.mapId === mapId) {
      useUIStore.getState().showToast('Bạn đang ở map này rồi', 'info');
      return;
    }

    const cli = getPitayaClient();
    if (!cli || !cli.isConnected()) {
      useUIStore.getState().showToast('Mất kết nối server', 'error');
      return;
    }

    try {
      const resp = await cli.request<TeleportResp>(currentRoute(), { mapId });
      if (!resp.ok) {
        const msg = mapDenyReason(resp.reason);
        useUIStore.getState().showToast(msg, 'warn');
        return;
      }

      set({ lastTeleportAt: Date.now() });
      useCharacterStore.getState().setCurrency({ zen: resp.newZen });
      get().beginChannel(mapId, cfg.channelMs);

      // Channel time: visual delay before scene swap
      await new Promise((r) => setTimeout(r, cfg.channelMs));

      // Update character map + position locally before scene switch.
      useCharacterStore.getState().setPosition({ x: resp.spawnX, y: resp.spawnY, mapId: resp.mapId });
      get().markVisited(resp.mapId);

      // Switch Phaser scene
      const game = getGame();
      const active = game?.scene.getScenes(true)[0];
      if (game && active) {
        const target = dest.scene;
        active.scene.start(target);
      }

      useUIStore.getState().set('teleport', false);
      useUIStore.getState().showToast(`Đã đến ${dest.name}`, 'info');
    } catch (e) {
      useUIStore.getState().showToast(`Teleport lỗi: ${(e as Error).message}`, 'error');
    } finally {
      get().cancelChannel();
    }
  },

  useTownScroll: async () => {
    const now = Date.now();
    if (now < get().townScrollEndsAt) {
      const left = Math.ceil((get().townScrollEndsAt - now) / 1000);
      useUIStore.getState().showToast(`Town Scroll cooldown ${left}s`, 'warn');
      return;
    }
    const ch = useCharacterStore.getState().current;
    if (!ch) return;
    if (ch.mapId === 1) {
      useUIStore.getState().showToast('Bạn đang ở Lorencia rồi', 'info');
      return;
    }

    const cli = getPitayaClient();
    if (!cli || !cli.isConnected()) {
      useUIStore.getState().showToast('Mất kết nối server', 'error');
      return;
    }

    try {
      const resp = await cli.request<TeleportResp>(currentRoute(), { mapId: 1, townScroll: true });
      if (!resp.ok) {
        useUIStore.getState().showToast(mapDenyReason(resp.reason), 'warn');
        return;
      }
      set({ townScrollEndsAt: now + cfg.townScrollCooldownMs, lastTeleportAt: now });
      useCharacterStore.getState().setCurrency({ zen: resp.newZen });
      get().beginChannel(1, 5000);
      await new Promise((r) => setTimeout(r, 5000));
      useCharacterStore.getState().setPosition({ x: resp.spawnX, y: resp.spawnY, mapId: 1 });
      get().markVisited(1);
      const game = getGame();
      const active = game?.scene.getScenes(true)[0];
      if (game && active) active.scene.start('Town');
      useUIStore.getState().set('teleport', false);
      useUIStore.getState().showToast('Town Scroll: về Lorencia', 'info');
    } catch (e) {
      useUIStore.getState().showToast(`Town Scroll lỗi: ${(e as Error).message}`, 'error');
    } finally {
      get().cancelChannel();
    }
  },
}));

function mapDenyReason(r?: string): string {
  switch (r) {
    case 'combat': return 'Không thể teleport khi đang chiến đấu';
    case 'pk': return 'Karma đỏ, chờ 60s';
    case 'no_zen': return 'Không đủ Zen';
    case 'cooldown': return 'Đang trong cooldown';
    case 'locked': return 'Map chưa mở khóa';
    case 'level': return 'Chưa đủ level';
    default: return r || 'Teleport bị từ chối';
  }
}
